import React, { useState, useEffect } from 'react';
import {
    Typography,
    FormControl,
    Select,
    MenuItem,
    Button,
    Box,
    Autocomplete,
    TextField,
    Drawer,
    IconButton,
    Divider,
    CircularProgress,
    Tooltip,
    InputAdornment
} from '@mui/material';
import { Close, FilterList, Search } from '@mui/icons-material';
import {  workFilter } from '../../services/Project';

const WorkFilter = ({ onFilterApply }) => {
    const [searchValue, setSearchValue] = useState('');
    const [sortOption, setSortOption] = useState('All');
    const [filteredLocation, setFilteredLocation] = useState('all');
    const [filteredWorkType, setFilteredworkType] = useState('all');
    const [filteredRoles, setFilteredRoles] = useState('all');
    const [filteredShootDate, setFilteredShootDate] = useState('all');
    const [isApplying, setIsApplying] = useState(false);

    // Filter data from API
    const [filtersLocation, setFiltersLocation] = useState([]);
    const [filtersWorkTypes, setFiltersWorkTypes] = useState([]);
    const [filtersSortBy, setFiltersSortBy] = useState([]);
    const [filtersRoles, setFiltersRoles] = useState([]);
    const [filtersShootDate, setFiltersShootDate] = useState([]);

    const [open, setOpen] = useState(false);

    const fetchFilters = async () => {
        try {
            const response = await workFilter();
            console.log("Fetched work filters:", response);
            setFiltersLocation(response.data?.locations || []);
            setFiltersWorkTypes(response.data?.work_project_types || []);
            setFiltersSortBy(response.data?.sort_by || []);
            setFiltersRoles(response.data?.work_roles || []);
            setFiltersShootDate(response.data?.shoot_dates || []);
        } catch (error) {
            console.error("Error fetching work filters:", error);
        }
    };

    useEffect(() => {
        fetchFilters();
    }, []);



    const handleSortChange = (event) => {
        const option = event.target.value;
        setSortOption(option);
        console.log(`Sorting by: ${option}`);
    };



    const handleLocationChange = (event) => {
        const location = event.target.value;
        setFilteredLocation(location);
        console.log(`Filtering by location: ${location}`);
    };


    const handleWorkTypeChange = (event) => {
        const workType = event.target.value;
        setFilteredworkType(workType);
        console.log(`Filtering by work type: ${workType}`);
    };


    const handleRolesChange = (event) => {
        const roles = event.target.value;
        setFilteredRoles(roles);
        console.log(`Filtering by roles: ${roles}`);
    };


    const handleShootDateChange = (event) => {
        const shootDate = event.target.value;
        setFilteredShootDate(shootDate);
        console.log(`Filtering by shoot date: ${shootDate}`);
    };


    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchValue(value);

        // If search value is empty, automatically apply filters to get all works
        if (value === '') {
            handleFilterWork();
        }
    };


    const handleSearchKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSearchSubmit();
        }
    };


    const handleSearchSubmit = () => {
        console.log(`Searching for: ${searchValue}`);
        // Trigger filter apply with search
        handleFilterWork();
    };


    const handleFilterWork = async () => {
        setIsApplying(true);

        const selectedSortOptionId = filtersSortBy.find(option => option.value === sortOption)?.id;
        const selectedLocationId = filtersLocation.find(option => `${option.city}, ${option.country}` === filteredLocation)?.id;
        const selectedWorkTypeId = filtersWorkTypes.find(option => option.project_type_name === filteredWorkType)?.work_project_type_id;
        const selectedRoleId = filtersRoles.find(option => option.role_name === filteredRoles)?.work_role_id;
        const selectedShootDateId = filtersShootDate.find(option => option.value === filteredShootDate)?.id;

        const queryParams = {
            search: searchValue || '',
            location: selectedLocationId || "all",
            work_type: selectedWorkTypeId || "all",
            role: selectedRoleId || "all",
            shoot_date: selectedShootDateId || "all",
            sort_by: selectedSortOptionId
        };

        const queryString = new URLSearchParams(queryParams).toString();

        if (onFilterApply) {
            await onFilterApply(queryString);
        }

        setIsApplying(false);
        setOpen(false);
    };



    const handleClearFilterWork = () => {
        setFilteredLocation('all');
        setFilteredworkType('all');
        setFilteredRoles('all');
        setFilteredShootDate('all');
        setSortOption('All');
        setSearchValue('');

        const queryParams = {
            search: '',
            location: "all",
            work_type: "all",
            role: "all",
            shoot_date: "all",
            sort_by: filtersSortBy.find(option => option.value === 'All')?.id
        };

        const queryString = new URLSearchParams(queryParams).toString();

        if (onFilterApply) {
            onFilterApply(queryString);
        }
    };

    const toggleDrawer = (newOpen) => () => {
        setOpen(newOpen);
    };



    // Generate applied filters label
    const getAppliedFiltersLabel = () => {
        const filters = [];

        if (sortOption !== 'All') filters.push(`Sort: ${sortOption}`);
        if (filteredLocation !== 'all') filters.push(`Location: ${filteredLocation}`);
        if (filteredWorkType !== 'all') filters.push(`Type: ${filteredWorkType}`);
        if (filteredRoles !== 'all') filters.push(`Role: ${filteredRoles}`);
        if (filteredShootDate !== 'all') filters.push(`Date: ${filteredShootDate}`);
        
        console.log("filteredLocation", filteredLocation);
        return filters.length > 0 ? `Applied - ${filters.join(', ')}` : '';
    };

    // Get the count of applied filters correctly
    const getAppliedFiltersCount = () => {
        let count = 0;

        if (sortOption !== 'All') count++;
        if (filteredLocation !== 'all') count++;
        if (filteredWorkType !== 'all') count++;
        if (filteredRoles !== 'all') count++;
        if (filteredShootDate !== 'all') count++;

        return count;
    };

    const appliedFiltersCount = getAppliedFiltersCount();
    

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Tooltip title={getAppliedFiltersLabel() || "No filters applied"}>
                    <Button
                        onClick={toggleDrawer(true)}
                        startIcon={<FilterList />}
                        variant={getAppliedFiltersLabel() ? "contained" : "text"}
                        color="primary"
                        disableElevation
                        size="small"
                        sx={{ textTransform: 'none', width: 'fit-content' }}
                    >
                        Filter jobs
                        {getAppliedFiltersLabel() && (
                            <Typography variant="caption" sx={{ ml: 1 }} component={'span'}>
                                ({appliedFiltersCount})
                            </Typography>
                        )}
                    </Button>
                </Tooltip>
            </Box>

            <Drawer
                anchor="right"
                open={open}
                onClose={toggleDrawer(false)}
                sx={{
                    zIndex: 1201,
                    "& .MuiDrawer-paper": {
                        width: "100%",
                        maxWidth: 280,
                        borderRadius: 0,
                    },
                }}
                ModalProps={{
                    keepMounted: true,
                }}
                variant="temporary"
                transitionDuration={300}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1 }}>
                    <Typography variant="h6">Filter Jobs</Typography>
                    <IconButton onClick={toggleDrawer(false)} size="small">
                        <Close />
                    </IconButton>
                </Box>
                <Divider />

                <Box padding={2} sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                }}>

                    {/* Search Bar */}
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search for jobs"
                        variant="outlined"
                        sx={{ mb: 2 }}
                        value={searchValue}
                        onKeyDown={handleSearchKeyDown}
                        onChange={handleSearchChange}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <>
                                        {searchValue && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    sx={{ padding: 0 }}
                                                    onClick={() => setSearchValue('')}
                                                >
                                                    <Close />
                                                </IconButton>
                                            </InputAdornment>
                                        )}
                                        <InputAdornment position="end">
                                            <Tooltip title="Search" arrow placement="top">
                                                <IconButton
                                                    sx={{ paddingRight: 0 }}
                                                    onClick={handleSearchSubmit}
                                                >
                                                    <Search />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    </>
                                ),
                            },
                        }}
                    />

                    {/* Sort By */}
                    <FormControl variant="outlined" fullWidth margin="dense" size="small">
                        <Typography variant="body2" gutterBottom>Sort By</Typography>
                        <Select value={sortOption} onChange={handleSortChange}>
                            {filtersSortBy.map((option) => (
                                <MenuItem key={option.id} value={option.value}>
                                    {option.value}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Filter By Section */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                        <Typography variant="body2">Filter By</Typography>
                        {(filteredLocation !== 'all' ||
                            sortOption !== 'All' ||
                            filteredWorkType !== 'all' ||
                            filteredRoles !== 'all' ||
                            filteredShootDate !== 'all') && (
                                <Button
                                    variant="text"
                                    size="small"
                                    color="error"
                                    sx={{ textTransform: 'none', py: 0, px: 0.5 }}
                                    onClick={handleClearFilterWork}
                                >
                                    Clear
                                </Button>
                            )}
                    </Box>

                    {/* Location Filter */}
                    <FormControl fullWidth margin="dense" size="small">
                        <Typography variant="caption">Location</Typography>
                        <Autocomplete
                            disablePortal
                            id="location-filter"
                            size="small"
                            options={[{ id: 'all', city: 'All', country: '' }, ...filtersLocation]}
                            getOptionLabel={(option) =>
                                option.city === 'All' ? 'All' : `${option.city}, ${option.country}`
                            }
                            value={
                                filteredLocation === 'all'
                                    ? { id: 'all', city: 'All', country: '' }
                                    : filtersLocation.find(
                                        (option) => `${option.city}, ${option.country}` === filteredLocation
                                    ) || null
                            }
                            onChange={(event, newValue) => {
                                if (newValue) {
                                    handleLocationChange({
                                        target: {
                                            value:
                                                newValue.city === 'All'
                                                    ? 'all'
                                                    : `${newValue.city}, ${newValue.country}`,
                                        },
                                    });
                                }
                            }}
                            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Select location..." />}
                        />
                    </FormControl>

                    {/* Work Type Filter */}
                    <FormControl variant="outlined" fullWidth margin="dense" size="small">
                        <Typography variant="caption">Work Type</Typography>
                        <Select value={filteredWorkType} onChange={handleWorkTypeChange}>
                            <MenuItem value="all">All</MenuItem>
                            {filtersWorkTypes.map((option) => (
                                <MenuItem key={option.work_project_type_id} value={option.project_type_name}>
                                    {option.project_type_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Roles Filter */}
                    <FormControl variant="outlined" fullWidth margin="dense" size="small">
                        <Typography variant="caption">Roles</Typography>
                        <Select
                            value={filteredRoles}
                            onChange={handleRolesChange}
                        >
                            <MenuItem value="all">All</MenuItem>
                            {filtersRoles.map((option) => (
                                <MenuItem key={option.work_role_id} value={option.role_name}>
                                    {option.role_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Shoot Date Filter */}
                    <FormControl variant="outlined" fullWidth margin="dense" size="small">
                        <Typography variant="caption">Shoot Date</Typography>
                        <Select
                            value={filteredShootDate}
                            onChange={handleShootDateChange}
                        >
                            <MenuItem value="all">All</MenuItem>
                            {filtersShootDate.map((option) => (
                                <MenuItem key={option.id} value={option.value}>
                                    {option.value}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Apply Filters Button */}
                    <Button
                        disableElevation
                        variant="contained"
                        color="primary"
                        fullWidth
                        sx={{ textTransform: 'none', mt: 2 }}
                        onClick={handleFilterWork}
                        disabled={isApplying}
                        startIcon={isApplying ? <CircularProgress size={20} /> : null}
                    >
                        {isApplying ? 'Applying...' : 'Apply Filters'}
                    </Button>
                </Box>
            </Drawer>
        </>
    );
};

export default WorkFilter;